import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Form, FormInstance, Tabs } from "antd";
import { bindActionCreators, Dispatch } from "redux";
import { Creators as AttendanceActions } from "store/attendance/attendance-duck";
import { IItemsCheckinDataTable } from "store/attendance/attendance-model";
import { Creators as LobbyActions } from "store/lobby/lobby-duck";
import {
  IProcessesData,
  IDocumentsData,
  IRegisterActiveItem,
  ISaleChannelData,
  IDocumentType,
  IStepEntryPermissionStatus,
  IEntryPermissionActive,
  PlateFormatType,
  FleetType,
  TrailerType,
} from "store/lobby/lobby-model";
import { Creators as VehicleDashboardActions } from "store/vehicle-dashboard/vehicle-dashboard-duck";
import { DrawerStatusEnum, TabPaneStatusEnum } from "typings/global.types";
import { getSaleChannelFormValue, mountRegisterFormValues } from "utils/form/saleChannel";
import { checkWhenDocumentsIsEmpty, searchProcessByIdSelected, searchProcessCorrelationIdSelectedById, searchSaleChannelByIdSelected } from "utils/functions/";
import useAfrDocumentsFlow from "utils/hooks/useAfrDocumentsFlow";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "../index.styled";
import FormContent from "./FormContent";
import ItemContent from "./ItemContent";

type LayoutType = Parameters<typeof Form>[0]["layout"];

export interface IFormCheckin {
  form: FormInstance | any;
  visibleDrawerViewModelAttendance?: DrawerStatusEnum;
  inboundProcessesList: IProcessesData[];
  salesChannelList?: Array<ISaleChannelData>;
  documentsList: IDocumentsData[];
  hasSaleChannel: boolean;
  drawerIsLoading: boolean;
  saleChannelActive?: number | null;
  documentType: IDocumentType;
  hasItems: boolean | any;
  plateFormatType?: PlateFormatType;
  itemsCheckin?: IItemsCheckinDataTable[];
  visibleDrawerCheckinRegister?: boolean;
  visibleDrawerViewCheckinRegisterAttendance?: boolean;
  visibleDrawerViewCheckinRegisterVehicleDashboard?: boolean;
  checkinRegisterActive?: IRegisterActiveItem;
  tabPaneActive?: TabPaneStatusEnum;
  hasEntryPermission: boolean;
  isLoadingEntryPermission?: boolean;
  stepEntryPermissionStatus?: IStepEntryPermissionStatus;
  entryPermissionActive?: IEntryPermissionActive;
  lobbyDocumentsFetchAsync?: (...args) => void;
  lobbyProcessActive?: (...args) => void;
  lobbySaleChannelActive?: (...args) => void;
  attendanceUpdateTabpaneActive?: (...args) => void;
  attendanceFetchItemsCheckinAsync?: (...args) => void;
  lobbyFetchEntryPermissionAsync?: (...args) => void;
  lobbyUpdateStepEntryPermissionStatus?: (...args) => void;
  lobbyUpdateEntryPermissionActive?: (...args) => void;
}

export const FormCheckin = ({
  form,
  drawerIsLoading,
  saleChannelActive,
  visibleDrawerViewModelAttendance,
  inboundProcessesList,
  salesChannelList,
  documentsList,
  checkinRegisterActive,
  visibleDrawerCheckinRegister,
  visibleDrawerViewCheckinRegisterAttendance,
  visibleDrawerViewCheckinRegisterVehicleDashboard,
  hasSaleChannel,
  hasItems,
  itemsCheckin,
  tabPaneActive,
  documentType,
  hasEntryPermission,
  stepEntryPermissionStatus,
  isLoadingEntryPermission,
  entryPermissionActive,
  plateFormatType,
  lobbyFetchEntryPermissionAsync,
  attendanceUpdateTabpaneActive,
  attendanceFetchItemsCheckinAsync,
  lobbyDocumentsFetchAsync,
  lobbyProcessActive,
  lobbySaleChannelActive,
  lobbyUpdateStepEntryPermissionStatus,
  lobbyUpdateEntryPermissionActive,
}: IFormCheckin) => {
  const isAfrDocumentsFlow = useAfrDocumentsFlow();
  const [formLayout, setFormLayout] = useState<LayoutType>("vertical");
  const [fieldsCount, setFieldsCount] = useState(1);
  const [visibleDocuments, setVisibleDocuments] = useState(false);
  const eventType = 1;
  const autoSizeInputText = { minRows: 4, maxRows: 13 };
  const INFORMATIONS = useTranslation({ id: "informations" });
  const ITEMS = useTranslation({ id: "items" });
  const checkDrawerIsLobbyOrAttendance = visibleDrawerCheckinRegister ? DrawerStatusEnum.Edit : visibleDrawerViewModelAttendance;
  const isEditingCheckin = Boolean(visibleDrawerCheckinRegister || (visibleDrawerViewCheckinRegisterAttendance && visibleDrawerViewModelAttendance === DrawerStatusEnum.Edit));
  const selectedSaleChannelId = Form.useWatch("saleChannelDescription", form);
  const selectedTrailerType = Form.useWatch("trailerType", form);

  const visibleViewDrawer = () => {
    if (visibleDrawerViewCheckinRegisterVehicleDashboard) return true;

    return Boolean(visibleDrawerViewCheckinRegisterAttendance && !isEditingCheckin);
  };
  const onFormLayoutChange = ({ layout }: { layout: LayoutType }) => {
    setFormLayout(layout);
  };

  const handleChangeProcess = (processDescription) => {
    lobbyProcessActive(processDescription);
    const processDescriptionActive = searchProcessByIdSelected(processDescription, inboundProcessesList);
    const processCorrelationId = searchProcessCorrelationIdSelectedById(processDescription, inboundProcessesList);
    form.setFieldsValue({ processDescriptionActive });
    lobbyDocumentsFetchAsync(processCorrelationId, eventType);
    setVisibleDocuments(true);
  };

  const handleChangeSalesChannel = (saleChannelId) => {
    if (saleChannelId !== FleetType.Others && form.getFieldValue("trailerType") === TrailerType.OTHER) {
      form.setFieldsValue({ trailerType: null });
    }
    lobbySaleChannelActive(saleChannelId);
    const saleChannelDescriptionActive = searchSaleChannelByIdSelected(saleChannelId, salesChannelList);
    form.setFieldsValue({ saleChannelDescriptionActive });
  };

  useEffect(() => {
    if (visibleDrawerCheckinRegister || visibleDrawerViewCheckinRegisterAttendance || visibleDrawerViewCheckinRegisterVehicleDashboard) {
      const processCorrelationIdActive = searchProcessCorrelationIdSelectedById(checkinRegisterActive?.processId, inboundProcessesList);
      processCorrelationIdActive && lobbyDocumentsFetchAsync(processCorrelationIdActive, eventType);
      lobbyProcessActive(checkinRegisterActive?.processId);
      lobbySaleChannelActive(getSaleChannelFormValue(checkinRegisterActive, hasSaleChannel, salesChannelList));
      checkWhenDocumentsIsEmpty(documentsList, checkinRegisterActive?.documents, form);
      setVisibleDocuments(true);
    }
  }, [
    visibleDrawerCheckinRegister,
    checkinRegisterActive,
    visibleDrawerViewCheckinRegisterVehicleDashboard,
    visibleDrawerViewCheckinRegisterAttendance,
    inboundProcessesList,
    salesChannelList,
  ]);

  useEffect(() => {
    if (checkinRegisterActive?.id) {
      form.setFieldsValue(mountRegisterFormValues(checkinRegisterActive, hasSaleChannel, salesChannelList, isEditingCheckin));
      if (hasItems && visibleDrawerViewCheckinRegisterAttendance && checkinRegisterActive.documents) {
        attendanceFetchItemsCheckinAsync(checkinRegisterActive.yardRegisterId);
      }
    }
  }, [checkinRegisterActive, hasSaleChannel, isEditingCheckin, salesChannelList]);

  useEffect(() => {
    if (isEditingCheckin && checkinRegisterActive?.id) {
      const requiredFields = ["carrier", "vehiclePlate", "processDescription", "trailerType"];
      if (hasSaleChannel) requiredFields.push("saleChannelDescription");

      form.validateFields(requiredFields);
    }
  }, [checkinRegisterActive, hasSaleChannel, isEditingCheckin, salesChannelList]);

  useEffect(() => {
    if (isEditingCheckin && selectedTrailerType === TrailerType.OTHER && selectedSaleChannelId !== FleetType.Others) {
      form.setFieldsValue({ trailerType: null });
    }
  }, [form, isEditingCheckin, selectedSaleChannelId, selectedTrailerType]);

  const handleTabPane = (key: string) => {
    if (key === TabPaneStatusEnum.Items) {
      attendanceUpdateTabpaneActive(TabPaneStatusEnum.Items);
    } else {
      attendanceUpdateTabpaneActive(TabPaneStatusEnum.Info);
    }
  };

  return (
    <S.DrawerContent itemsCheckin={itemsCheckin} tabPaneActive={tabPaneActive}>
      {hasItems ? (
        <Tabs defaultActiveKey="1" style={{ height: "100%" }} onChange={handleTabPane}>
          <Tabs.TabPane tab={INFORMATIONS} key="1" data-testid="tab-data" style={{ height: "100%" }}>
            <FormContent
              form={form}
              formLayout={formLayout}
              drawerIsLoading={drawerIsLoading}
              autoSizeInputText={autoSizeInputText}
              onFormLayoutChange={onFormLayoutChange}
              fieldsCount={fieldsCount}
              setFieldsCount={setFieldsCount}
              salesChannelList={salesChannelList}
              visibleViewDrawer={visibleViewDrawer}
              visibleDrawerViewMode={checkDrawerIsLobbyOrAttendance}
              visibleDocuments={visibleDocuments}
              hasSaleChannel={hasSaleChannel}
              handleChangeSalesChannel={handleChangeSalesChannel}
              handleChangeProcess={handleChangeProcess}
              inboundProcessesList={inboundProcessesList}
              documentsList={documentsList}
              eventType={eventType}
              checkinRegisterActive={checkinRegisterActive}
              documentType={documentType}
              visibleDrawerCheckinRegister={visibleDrawerCheckinRegister}
              hasEntryPermission={hasEntryPermission}
              lobbyFetchEntryPermissionAsync={lobbyFetchEntryPermissionAsync}
              stepEntryPermissionStatus={stepEntryPermissionStatus}
              isLoadingEntryPermission={isLoadingEntryPermission}
              entryPermissionActive={entryPermissionActive}
              lobbyUpdateStepEntryPermissionStatus={lobbyUpdateStepEntryPermissionStatus}
              lobbyUpdateEntryPermissionActive={lobbyUpdateEntryPermissionActive}
              saleChannelActive={saleChannelActive}
              selectedSaleChannelId={selectedSaleChannelId}
              plateFormatType={plateFormatType}
              isAfrDocumentsFlow={isAfrDocumentsFlow}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab={ITEMS} key="2" data-testid="tab-items">
            <ItemContent itemsCheckin={itemsCheckin} />
          </Tabs.TabPane>
        </Tabs>
      ) : (
        <FormContent
          form={form}
          formLayout={formLayout}
          onFormLayoutChange={onFormLayoutChange}
          fieldsCount={fieldsCount}
          setFieldsCount={setFieldsCount}
          salesChannelList={salesChannelList}
          autoSizeInputText={autoSizeInputText}
          hasSaleChannel={hasSaleChannel}
          handleChangeSalesChannel={handleChangeSalesChannel}
          handleChangeProcess={handleChangeProcess}
          inboundProcessesList={inboundProcessesList}
          visibleDocuments={visibleDocuments}
          documentsList={documentsList}
          eventType={eventType}
          visibleViewDrawer={visibleViewDrawer}
          checkinRegisterActive={checkinRegisterActive}
          visibleDrawerViewMode={checkDrawerIsLobbyOrAttendance}
          drawerIsLoading={drawerIsLoading}
          documentType={documentType}
          visibleDrawerCheckinRegister={visibleDrawerCheckinRegister}
          hasEntryPermission={hasEntryPermission}
          lobbyFetchEntryPermissionAsync={lobbyFetchEntryPermissionAsync}
          stepEntryPermissionStatus={stepEntryPermissionStatus}
          isLoadingEntryPermission={isLoadingEntryPermission}
          entryPermissionActive={entryPermissionActive}
          lobbyUpdateStepEntryPermissionStatus={lobbyUpdateStepEntryPermissionStatus}
          lobbyUpdateEntryPermissionActive={lobbyUpdateEntryPermissionActive}
          saleChannelActive={saleChannelActive}
          selectedSaleChannelId={selectedSaleChannelId}
          plateFormatType={plateFormatType}
          isAfrDocumentsFlow={isAfrDocumentsFlow}
        />
      )}
    </S.DrawerContent>
  );
};

const mapDispatchToActions = (dispatch: Dispatch) => bindActionCreators({ ...LobbyActions, ...AttendanceActions, ...VehicleDashboardActions }, dispatch);

const mapStateToProps = (state) => ({
  isLoading: state.Lobby.isLoading,
  checkinRegisterActive: state.Lobby.checkinRegisterActive,
  visibleDrawerCheckinRegister: state.Lobby.visibleDrawerCheckinRegister,
  inboundProcessesList: state.Lobby.inboundProcessesList,
  salesChannelList: state.Lobby.salesChannelList,
  visibleDrawerViewCheckinRegisterAttendance: state.Attendance.visibleDrawerViewCheckinRegisterAttendance,
  visibleDrawerViewCheckinRegisterVehicleDashboard: state.VehicleDashboard.visibleDrawerViewCheckinRegisterVehicleDashboard,
  hasSaleChannel: state.Lobby.hasSaleChannel,
  visibleDrawerViewModelAttendance: state.Attendance.visibleDrawerViewMode,
  visibleDrawerViewModeLobby: state.Lobby.visibleDrawerViewMode,
  documentsList: state.Lobby.documentsList,
  itemsCheckin: state.Attendance.itemsCheckin,
  tabPaneActive: state.Attendance.tabPaneActive,
  registerActive: state.Lobby.registerActive,
  drawerIsLoading: state.Lobby.drawerIsLoading,
  documentType: state.Lobby.documentType,
  hasEntryPermission: state.Lobby.hasEntryPermission,
  stepEntryPermissionStatus: state.Lobby.stepEntryPermissionStatus,
  isLoadingEntryPermission: state.Lobby.isLoadingEntryPermission,
  entryPermissionActive: state.Lobby.entryPermissionActive,
  saleChannelActive: state.Lobby.saleChannelActive,
  plateFormatType: state.Lobby.plateFormatType,
});

export default connect(mapStateToProps, mapDispatchToActions)(FormCheckin);
